// Cross-section fan-out review modal (session 96; s97 polish). Lists each section the
// uploaded document produced data for, as a collapsible card with the extracted
// field→value pairs. The planner populates per section (or all at once); each populate
// appends NEW rows to that section — it never overwrites existing data.
//
// s97 Fix 3: per-RECORD selection. Every record carries a checkbox (checked by
// default); the planner can uncheck records they don't want. Populate (per section or
// all) sends only the checked record indexes to the host. Presentational + controlled:
// the host owns the populate network calls and the populated/saving state.

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, ChevronRight, Loader2, AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import type { FanOutSection } from "@/components/FanOutBanner";
import { CLAY, WARM } from "@shared/brand";

/** Per-section selected record indexes the host sends to populate-section. */
export interface FanOutSelection {
  sectionId: string;
  rowIndexes: number[];
}

interface FanOutReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sections: FanOutSection[];
  /** Section ids already populated this session (green check, button disabled). */
  populatedSectionIds: Set<string>;
  /** Section id currently being written (spinner), or null. */
  populatingSectionId: string | null;
  onPopulate: (sectionId: string, rowIndexes: number[]) => void;
  onPopulateAll: (selections: FanOutSelection[]) => void;
  /** Populate All in flight → button shows a "Populating…" spinner. */
  populatingAll?: boolean;
  /** After Populate All completes, the number of sections written → button shows
   *  "Data added to N sections ✓" briefly before the host closes the modal. */
  allDoneCount?: number | null;
  /** DB-45 1I: this file was already used to populate fan-out for this client →
   *  populating again duplicates everything. Shows a clay warning + makes the action
   *  deliberate ("Populate Anyway"); the default (Close) is to not duplicate. */
  reuploadWarning?: boolean;
}

export function FanOutReviewModal({
  open,
  onOpenChange,
  sections,
  populatedSectionIds,
  populatingSectionId,
  onPopulate,
  onPopulateAll,
  populatingAll = false,
  allDoneCount = null,
  reuploadWarning = false,
}: FanOutReviewModalProps) {
  // Sections start expanded so the planner sees what would be written.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Per-record selection, keyed by sectionId → set of selected row indexes. Defaults
  // to every record checked; re-initialised whenever a new fan-out's sections arrive.
  const [selected, setSelected] = useState<Record<string, Set<number>>>({});
  useEffect(() => {
    const init: Record<string, Set<number>> = {};
    for (const s of sections) init[s.sectionId] = new Set(s.rows.map((_, i) => i));
    setSelected(init);
  }, [sections]);

  const selectedIdx = (sectionId: string): number[] =>
    Array.from(selected[sectionId] ?? []).sort((a, b) => a - b);
  const selectedCount = (sectionId: string) => selected[sectionId]?.size ?? 0;

  const toggleRecord = (sectionId: string, idx: number) =>
    setSelected((prev) => {
      const next = { ...prev };
      const set = new Set(next[sectionId] ?? []);
      set.has(idx) ? set.delete(idx) : set.add(idx);
      next[sectionId] = set;
      return next;
    });

  const allDone = sections.length > 0 && sections.every((s) => populatedSectionIds.has(s.sectionId));
  // Populate All targets every not-yet-populated section that has ≥1 record checked.
  const anySelectedToPopulate = sections.some(
    (s) => !populatedSectionIds.has(s.sectionId) && selectedCount(s.sectionId) > 0,
  );

  const handlePopulateAll = () => {
    const selections: FanOutSelection[] = sections
      .filter((s) => !populatedSectionIds.has(s.sectionId) && selectedCount(s.sectionId) > 0)
      .map((s) => ({ sectionId: s.sectionId, rowIndexes: selectedIdx(s.sectionId) }));
    onPopulateAll(selections);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Populate other sections</DialogTitle>
          <DialogDescription>
            Your document contains information for these sections. Uncheck any records you don't want,
            then add them — this appends new records and never overwrites what you already have.
          </DialogDescription>
        </DialogHeader>

        {reuploadWarning && (
          <div
            className="flex items-start gap-2 rounded-md border px-3 py-2 text-[13px]"
            style={{ backgroundColor: CLAY[100], borderColor: CLAY[400], color: CLAY[800] }}
            data-testid="fanout-reupload-warning"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: CLAY[600] }} aria-hidden="true" />
            <span>
              You've already added data from this document. Populating again will create
              duplicate entries — close this if you didn't mean to re-upload, or choose
              "Populate Anyway" to add them again.
            </span>
          </div>
        )}

        <div className="space-y-3">
          {sections.map((section) => {
            const isPopulated = populatedSectionIds.has(section.sectionId);
            const isPopulating = populatingSectionId === section.sectionId;
            const isCollapsed = collapsed.has(section.sectionId);
            const recordCount = section.rows.length;
            const chosen = selectedCount(section.sectionId);
            return (
              <div
                key={section.sectionId}
                className="border rounded-lg overflow-hidden"
                data-testid={`fanout-section-${section.sectionSlug}`}
              >
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/40">
                  <button
                    className="flex items-center gap-2 text-sm font-medium flex-1 text-left"
                    onClick={() => toggle(section.sectionId)}
                  >
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                    {section.sectionName}
                    {/* DB-45 1I-B: muted heads-up. When the section already has records,
                        show "N already · adding M"; informational only — nothing is
                        auto-skipped on this count. */}
                    <span
                      className="text-xs font-normal"
                      style={{ color: WARM.taupe }}
                      data-testid={`fanout-existing-${section.sectionSlug}`}
                    >
                      {section.existingRecordCount && section.existingRecordCount > 0
                        ? `${section.existingRecordCount} record${section.existingRecordCount !== 1 ? "s" : ""} already · adding ${recordCount}`
                        : `${recordCount} record${recordCount !== 1 ? "s" : ""}`}
                    </span>
                  </button>
                  {isPopulated ? (
                    <span className="flex items-center gap-1 text-xs font-medium text-sage-700">
                      <Check className="h-4 w-4" /> Added
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isPopulating || populatingSectionId !== null || chosen === 0}
                      onClick={() => onPopulate(section.sectionId, selectedIdx(section.sectionId))}
                      data-testid={`button-populate-${section.sectionSlug}`}
                    >
                      {isPopulating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `Populate ${section.sectionName}`}
                    </Button>
                  )}
                </div>

                {!isCollapsed && (
                  <div className="px-3 py-2 space-y-3">
                    {section.rows.map((row, i) => {
                      const checked = selected[section.sectionId]?.has(i) ?? false;
                      return (
                        <label
                          key={i}
                          className="flex items-start gap-2 cursor-pointer"
                          data-testid={`fanout-record-${section.sectionSlug}-${i}`}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 shrink-0 accent-sage-600"
                            checked={checked}
                            disabled={isPopulated}
                            onChange={() => toggleRecord(section.sectionId, i)}
                            data-testid={`checkbox-fanout-${section.sectionSlug}-${i}`}
                          />
                          <div className="flex-1 space-y-1">
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                              Record {i + 1}
                            </p>
                            {row.fields.map((f) => (
                              <div key={f.fieldId} className="flex items-start gap-2 text-sm">
                                <span className="text-muted-foreground w-40 shrink-0">{f.name}</span>
                                <span className="flex-1 break-words">{f.value}</span>
                              </div>
                            ))}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="button-fanout-close">
            {allDone ? "Done" : "Close"}
          </Button>
          <Button
            onClick={handlePopulateAll}
            disabled={
              allDone || populatingSectionId !== null || populatingAll ||
              allDoneCount !== null || !anySelectedToPopulate
            }
            data-testid="button-fanout-populate-all"
          >
            {allDoneCount !== null ? (
              <span className="flex items-center gap-1.5">
                <Check className="h-4 w-4" />
                Data added to {allDoneCount} section{allDoneCount !== 1 ? "s" : ""}
              </span>
            ) : populatingAll ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-4 w-4 animate-spin" />
                Populating…
              </span>
            ) : reuploadWarning ? (
              "Populate Anyway"
            ) : (
              "Populate All"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
